import { FormControl, Grid } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { MaskedField } from '@components/MaskedField';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { BRAZIL_UFS, formatCep, ufFromAbbreviation } from '@functions/brazil-documents';
import { SearchCepService } from '@services/search-cep.service';
import { addressQuery, mapsDirectionsUrl, mapsSearchUrl } from '@functions/google-maps';

const UF_OPTIONS = BRAZIL_UFS.map((uf) => ({ value: String(uf.value), label: uf.label }));

/// Bloco de endereço com busca de CEP e mapa, dentro de formulário com AddressFormValues.
export const AddressFields = () => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const { setValue, getValues, watch } = useFormContext();

    const [streetName, addressNumber, district, city, state, postalCode] =
        watch(['streetName', 'number', 'district', 'city', 'state', 'postalCode']);

    /// Endereço como o Google Maps entende; vazio sem rua nem cidade, e aí os botões ficam desligados.
    const mapQuery = addressQuery({
        streetName,
        number: addressNumber,
        district,
        city,
        uf: BRAZIL_UFS.find((uf) => String(uf.value) === state)?.label,
        postalCode,
    });

    const openMap = (url: string) => window.open(url, '_blank', 'noopener');

    /// CEP geral de cidade volta sem rua e bairro; na edição, não pode apagar o já escrito.
    const searchCep = async (cep: string) => {
        if (cep.length !== 8) return;

        setLoading(dispatch, true);

        try {
            const { data } = await new SearchCepService().search(cep);

            if (data.erro) {
                showAlert(t('people.cepNaoEncontrado'), 'warning', 3000);
                return;
            }

            if (data.logradouro || !getValues('streetName')) setValue('streetName', data.logradouro ?? '');
            if (data.bairro || !getValues('district')) setValue('district', data.bairro ?? '');

            setValue('city', data.localidade ?? '');
            setValue('state', String(ufFromAbbreviation(data.uf) ?? ''));
        } catch {
            showAlert(t('people.cepIndisponivel'), 'warning', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <>
            <Form.Field size={2}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.cep')}</Form.Label>
                    <MaskedField
                        name="postalCode"
                        size="small"
                        fullWidth
                        mask={formatCep}
                        maxDigits={8}
                        onBlurDigits={searchCep}
                        placeholder="00000-000"
                    />
                    <Form.ErrorMessage field="postalCode" />
                </FormControl>
            </Form.Field>
            <Form.Field size={5}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.rua')}</Form.Label>
                    <Form.TextFields name="streetName" size="small" inputProps={{ maxLength: 200 }} placeholder={t('people.phRua')} />
                    <Form.ErrorMessage field="streetName" />
                </FormControl>
            </Form.Field>
            <Form.Field size={2}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.numero')}</Form.Label>
                    <Form.TextFields name="number" size="small" inputProps={{ maxLength: 20 }} placeholder={t('people.phNumero')} />
                </FormControl>
            </Form.Field>
            <Form.Field size={3}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.complemento')}</Form.Label>
                    <Form.TextFields name="complement" size="small" inputProps={{ maxLength: 100 }} placeholder={t('people.phComplemento')} />
                </FormControl>
            </Form.Field>
            <Form.Field size={4}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.bairro')}</Form.Label>
                    <Form.TextFields name="district" size="small" inputProps={{ maxLength: 100 }} placeholder={t('people.phBairro')} />
                </FormControl>
            </Form.Field>
            <Form.Field size={5}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.cidade')}</Form.Label>
                    <Form.TextFields name="city" size="small" inputProps={{ maxLength: 100 }} placeholder={t('people.phCidade')} />
                    <Form.ErrorMessage field="city" />
                </FormControl>
            </Form.Field>
            <Form.Field size={3}>
                <FormControl fullWidth>
                    <Form.Label>{t('people.uf')}</Form.Label>
                    <Form.AutoCompleteSelect name="state" options={UF_OPTIONS} size="small" />
                    <Form.ErrorMessage field="state" />
                </FormControl>
            </Form.Field>

            {/* O mapa fecha a seção: só faz sentido com o endereço já preenchido acima. */}
            <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    startIcon={<MapOutlinedIcon />}
                    disabled={!mapQuery}
                    onClick={() => openMap(mapsSearchUrl(mapQuery))}
                >
                    {t('people.verNoMapa')}
                </MDButton>
                <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    startIcon={<DirectionsOutlinedIcon />}
                    disabled={!mapQuery}
                    onClick={() => openMap(mapsDirectionsUrl(mapQuery))}
                >
                    {t('people.tracarRota')}
                </MDButton>
            </Grid>
        </>
    );
};
